import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error details for debugging
    console.group('🚨 Error Boundary Details');
    console.error('Error:', error.message);
    console.error('Stack:', error.stack);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <>
          <style>{`
            .error-boundary {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 200px;
              padding: var(--spacing-20);
              background: var(--slate-high);
              border: 1px solid var(--error);
              border-radius: var(--radius-lg);
              margin: var(--spacing-16);
              text-align: center;
            }
            
            .error-boundary-icon {
              font-size: 48px;
              margin-bottom: var(--spacing-16);
              color: var(--error);
            }
            
            .error-boundary-title {
              font-size: var(--font-size-xl);
              font-weight: var(--font-weight-bold);
              color: var(--text-primary);
              margin-bottom: var(--spacing-8);
            }
            
            .error-boundary-message {
              font-size: var(--font-size-md);
              color: var(--text-secondary);
              margin-bottom: var(--spacing-20);
              max-width: 400px;
              line-height: var(--line-height-relaxed);
            }
            
            .error-boundary-details {
              font-family: var(--font-mono);
              font-size: var(--font-size-sm);
              color: var(--text-tertiary);
              background: var(--slate-medium);
              padding: var(--spacing-12);
              border-radius: var(--radius-md);
              margin-bottom: var(--spacing-20);
              max-width: 600px;
              word-break: break-all;
              text-align: left;
            }
            
            .error-boundary-actions {
              display: flex;
              gap: var(--spacing-12);
            }
            
            .error-boundary-button {
              background: var(--glacier-blue);
              color: var(--white);
              border: none;
              padding: var(--spacing-12) var(--spacing-20);
              border-radius: var(--radius-md);
              font-size: var(--font-size-md);
              font-weight: var(--font-weight-medium);
              cursor: pointer;
              transition: all var(--transition-fast);
            }
            
            .error-boundary-button:hover {
              background: var(--glacier-blue-dark);
              transform: translateY(-1px);
            }
            
            .error-boundary-button--secondary {
              background: var(--slate-medium);
              color: var(--text-primary);
            }
            
            .error-boundary-button--secondary:hover {
              background: var(--slate-low);
            }
          `}</style>
          
          <div className="error-boundary">
            <div className="error-boundary-icon">⚠️</div>
            <h2 className="error-boundary-title">Something went wrong</h2>
            <p className="error-boundary-message">
              An unexpected error occurred. This has been logged and will be investigated.
            </p>
            
            {this.state.error && (
              <details className="error-boundary-details">
                <summary style={{ marginBottom: '8px', cursor: 'pointer' }}>
                  Technical Details
                </summary>
                <pre>{this.state.error.message}</pre>
              </details>
            )}
            
            <div className="error-boundary-actions">
              <button 
                className="error-boundary-button"
                onClick={this.handleRetry}
              >
                Try Again
              </button>
              <button 
                className="error-boundary-button error-boundary-button--secondary"
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </button>
            </div>
          </div>
        </>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
